import { useRef, useState } from 'react'
import { SOURCE_LABEL, sourceOf } from './evidence'
import { stepTitle } from './EvidenceMap'
import type { InvestigationResult, InvestigationStep } from '@/types/investigation'

function took(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

/**
 * Layout C from the approved mockup: every step on the left, the selected one's full arguments and
 * raw output on the right, with the findings that cite it.
 */
export function StepsInspector({ steps, result }: { steps: InvestigationStep[]; result?: InvestigationResult | null }) {
  const [selected, setSelected] = useState(0)
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  if (!steps.length) return <p className="py-6 text-sm text-etn-text-soft">This run made no tool calls.</p>

  const step = steps[Math.min(selected, steps.length - 1)]
  const usedIn = (result?.findings ?? []).filter(f => f.evidence.includes(selected)).map(f => f.title)

  return (
    <div className="grid min-h-[460px] grid-cols-1 overflow-hidden rounded-etn-card border border-etn-border-hairline md:grid-cols-[300px_minmax(0,1fr)]">
      <div
        role="tablist"
        aria-label="Investigation steps"
        aria-orientation="vertical"
        className="max-h-[260px] overflow-y-auto border-b border-etn-border-hairline bg-etn-surface-sunken p-2 md:max-h-none md:border-b-0 md:border-r"
      >
        {steps.map((s, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            id={`step-tab-${i}`}
            ref={el => {
              tabs.current[i] = el
            }}
            aria-selected={i === selected}
            aria-controls="step-detail"
            tabIndex={i === selected ? 0 : -1}
            onClick={() => setSelected(i)}
            onKeyDown={e => {
              const next = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: steps.length - 1 }[e.key]
              if (next === undefined) return
              e.preventDefault()
              const target = (next + steps.length) % steps.length
              setSelected(target)
              tabs.current[target]?.focus()
            }}
            className={`grid w-full grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left text-etn-control ${
              i === selected ? 'bg-etn-surface font-semibold shadow-etn-whisper' : 'text-etn-text-cell hover:bg-etn-surface'
            }`}
          >
            <span className="font-mono text-etn-micro font-bold text-etn-text-soft">{SOURCE_LABEL[sourceOf(s.tool)]}</span>
            <span className="truncate">{stepTitle(s)}</span>
            <span className="text-etn-micro tabular-nums text-etn-text-soft">
              {s.isError ? 'Skipped ' : ''}
              {took(s.durationMs)}
            </span>
          </button>
        ))}
      </div>
      <div id="step-detail" role="tabpanel" aria-labelledby={`step-tab-${selected}`} className="min-w-0 p-5" data-testid="step-detail">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
          {stepTitle(step)}
          {step.isError && (
            <span className="rounded-etn-pill bg-etn-danger-subtle px-2 py-0.5 text-etn-micro font-semibold text-etn-danger-text">Skipped</span>
          )}
        </h3>
        {step.why && <p className="mb-3.5 max-w-[70ch] text-etn-control text-etn-text-label">{step.why}</p>}
        <dl className="mb-3.5 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-etn-control">
          <dt className="text-etn-text-soft">Tool</dt>
          <dd className="font-mono text-xs">{step.tool}</dd>
          <dt className="text-etn-text-soft">Step</dt>
          <dd>{selected}</dd>
          <dt className="text-etn-text-soft">Took</dt>
          <dd>{took(step.durationMs)}</dd>
        </dl>
        <p className="mb-1.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Arguments</p>
        <pre className="mb-3.5 overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-etn-surface-sunken p-3 font-mono text-xs">
          {JSON.stringify(step.args ?? {}, null, 2)}
        </pre>
        <p className="mb-1.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Result</p>
        <pre
          className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-etn-surface-sunken p-3 font-mono text-xs"
          data-testid="step-output"
        >
          {step.resultPreview}
        </pre>
        {usedIn.length > 0 && (
          <div className="mt-3.5 flex flex-wrap items-center gap-2 text-xs text-etn-text-label" data-testid="used-in">
            Used in:
            {usedIn.map(title => (
              <span key={title} className="rounded-etn-pill bg-etn-success-subtle px-2.5 py-0.5 font-semibold text-etn-success-text">
                {title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
